import { useState } from "react";
import { BookOpen, Download, FileText, Loader2, ExternalLink, Wrench } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const MANUALS = {
  general: {
    label: "Manual General",
    title: "Manual de Usuario",
    subtitle: "Guía completa de la plataforma — todas las funcionalidades con capturas reales.",
    viewPath: "/manual/view",
    downloadPath: "/admin/manual",
    filename: "Manual_Valoriza",
  },
  taller: {
    label: "Manual de Taller",
    title: "Manual de Usuario — Taller",
    subtitle: "Guía del módulo de Taller (Parque Central de Maquinaria): carga masiva, firma digital, fotos, QR, avisos de mantenimiento e historial por máquina.",
    viewPath: "/manual/taller/view",
    downloadPath: null, // public-only, no auth PDF variant needed
    publicPdfPath: "/manual/taller/pdf",
    filename: "Manual_Taller_Parque_Maquinaria",
  },
};

export default function Manual() {
  const { authAxios, user } = useAuth();
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingHtml, setLoadingHtml] = useState(false);
  const [manualType, setManualType] = useState(
    user?.contract_ids?.includes("contract-parque-maquinaria") ? "taller" : "general"
  );

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const manual = MANUALS[manualType];
  const viewerSrc = `${backendUrl}/api${manual.viewPath}`;

  const handleDownload = async (type) => {
    const isHtml = type === "html";
    if (isHtml) setLoadingHtml(true); else setLoadingPdf(true);
    try {
      if (manual.downloadPath) {
        const response = await authAxios.get(`${manual.downloadPath}/${type}`, { responseType: "blob" });
        const blob = new Blob([response.data], { type: isHtml ? "text/html" : "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${manual.filename}.${type}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        window.open(`${backendUrl}/api${manual.publicPdfPath}`, "_blank");
      }
    } catch (err) {
      console.error("Manual download error:", err);
      alert("Error al descargar el manual. Inténtalo de nuevo.");
    } finally {
      if (isHtml) setLoadingHtml(false); else setLoadingPdf(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="manual-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 flex items-center gap-3">
            <BookOpen size={28} className="text-[#007934]" />
            {manual.title}
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            {manual.subtitle}
          </p>
        </div>

        <div className="flex gap-2">
          <button
            data-testid="manual-download-pdf"
            onClick={() => handleDownload("pdf")}
            disabled={loadingPdf}
            className="flex items-center gap-2 px-4 py-2 bg-[#007934] hover:bg-[#005c27] text-white font-semibold rounded-lg shadow-sm transition disabled:opacity-50 text-sm"
          >
            {loadingPdf ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            {loadingPdf ? "Generando PDF..." : "Descargar PDF"}
          </button>
          {manual.downloadPath && (
            <button
              data-testid="manual-download-html"
              onClick={() => handleDownload("html")}
              disabled={loadingHtml}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold rounded-lg shadow-sm transition disabled:opacity-50 text-sm"
            >
              {loadingHtml ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
              {loadingHtml ? "Descargando..." : "Descargar HTML"}
            </button>
          )}
          <a
            data-testid="manual-open-new-tab"
            href={viewerSrc}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold rounded-lg shadow-sm transition text-sm"
          >
            <ExternalLink size={15} />
            Abrir en pestaña
          </a>
        </div>
      </div>

      {/* Manual selector tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          data-testid="manual-tab-general"
          onClick={() => setManualType("general")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
            manualType === "general" ? "border-[#007934] text-[#007934]" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <BookOpen size={15} />
          Manual General
        </button>
        <button
          data-testid="manual-tab-taller"
          onClick={() => setManualType("taller")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
            manualType === "taller" ? "border-[#007934] text-[#007934]" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <Wrench size={15} />
          Manual de Taller
        </button>
      </div>

      {/* Viewer */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" style={{ height: "calc(100vh - 260px)" }}>
        <iframe
          data-testid="manual-iframe"
          src={viewerSrc}
          title={manual.title}
          className="w-full h-full border-0"
        />
      </div>
    </div>
  );
}
